import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useBuyGuide } from "../hooks/useBuyGuide";

const WORDS = ["automated", "elevated", "fabulous"];

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Hero() {
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const { loading: buyLoading, buyGuide } = useBuyGuide();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.4]);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % WORDS.length), 3400);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      ref={ref}
      id="hero-section"
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-16 md:pb-24 px-6 md:px-12 lg:px-20 overflow-hidden"
      style={{ background: "#FAF9F6" }}
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
          className="md:col-span-7 relative z-10"
        >
          <span className="overline" data-testid="hero-overline">Findaura Ventures · AI Education for Women</span>

          <h1 className="font-serif font-light tracking-tight leading-[0.95] mt-6 text-[3rem] sm:text-[4.5rem] md:text-[5.5rem] lg:text-[7rem]" data-testid="hero-headline">
            The future is
            <br />
            <span className="inline-block relative align-top" style={{ minWidth: "1ch" }}>
              <AnimatePresence mode="wait">
                <motion.em
                  key={WORDS[idx]}
                  initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: -24, filter: "blur(6px)" }}
                  transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                  className="inline-block italic"
                  style={{ color: "#C28C55" }}
                  data-testid={`hero-word-${WORDS[idx]}`}
                >
                  {WORDS[idx]}
                </motion.em>
              </AnimatePresence>
              <span className="italic" style={{ color: "#C28C55" }}>.</span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 md:mt-10 max-w-xl text-[15px] md:text-[17px] leading-relaxed text-[#5C5A56]"
            data-testid="hero-subhead"
          >
            An AI education house for women building softer, richer, more elevated lives —
            with prompts, playbooks, and quiet automations that do the heavy lifting so you don't have to.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.6 }}
            className="mt-10 md:mt-12 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={buyGuide}
              disabled={buyLoading}
              className="btn-primary"
              data-testid="hero-buy-now"
            >
              {buyLoading ? "Opening Checkout…" : "Buy the Soft Life AI Guide"}
              <span aria-hidden>→</span>
            </button>
            <button
              onClick={() => scrollToId("email-capture")}
              className="btn-outline"
              data-testid="hero-cta-freebie"
            >
              Free Prompt Guide
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y, opacity }}
          className="md:col-span-5 relative hero-image-wrap"
          data-testid="hero-image"
        >
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1606752445013-eeb8cc06aabd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxjdXJseSUyMGhhaXIlMjB3b21hbiUyMGJlaWdlJTIwYWVzdGhldGljfGVufDB8fHx8MTc4MzkwNTQxNHww&ixlib=rb-4.1.0&q=85"
              alt="Editorial luxury"
              className="w-full h-[70vh] md:h-[85vh] object-cover"
              loading="eager"
            />
            <div className="absolute -bottom-6 -left-6 hidden md:block">
              <div className="bg-[#FAF9F6] px-6 py-4 border border-[#D4AF37]/30">
                <span className="overline">Est. 2026</span>
                <div className="font-serif italic text-2xl mt-1">Findaura</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
